/**
 * Per-chat-client browser storage for the chatbot widget.
 *
 * The widget runs inside the host page, so everything it stores belongs to the
 * customer's website and is shared by every chatbot instance there. Each chat
 * client therefore gets its own names, suffixed with its api key, which is
 * globally unique (`Chat.apiKey` is `@Column(length=36, unique=true)`); without
 * that, a second chat client on the same site restored the first one's session
 * (#2503).
 *
 * State lives in host-only cookies with a browser-enforced lifetime (#2983), so
 * the session id disappears even when the visitor never returns. The lifetime is
 * sliding: the caller renews it on every sign of an active session.
 */

/** Lifetime of the session and closed-state cookies, renewed on activity. Matches the relay's idle `SESSION_TTL`. */
export const SESSION_MAX_AGE_SECONDS = 60 * 60;

/** Lifetime of the auto-open flag, which only has to survive one chatbot-triggered navigation. */
export const AUTO_OPEN_MAX_AGE_SECONDS = 60;

/** localStorage key names used before session ids were scoped per chat client (#2503). */
export const LEGACY_KEYS = {
  session: 'omq_chatbot_session_id',
  autoOpen: 'omq_chatbot_auto_open',
  userClosed: 'omq_chatbot_user_closed',
};

/** The cookie names belonging to one chat client. */
export interface ChatbotCookieNames {
  session: string;
  userClosed: string;
  autoOpen: string;
}

/** Product prefix on chat api keys, e.g. `CH-18d72c78-…`. */
const CHAT_API_KEY_PREFIX = 'CH-';

/**
 * Strips the product prefix from a chat api key.
 *
 * The backend resolves `CH-<uuid>` and `<uuid>` to the same chat client
 * (`ChatSec` calls `ApiKeyHelper.removePrefix` before the lookup), so two pages
 * embedding the same widget may spell the key differently. Normalizing keeps
 * both spellings on one name — and matching the relay's own normalization keeps
 * the two sides in agreement about session ownership.
 *
 * @param apiKey chat client api key as passed to the widget
 * @returns the api key without its product prefix
 */
const normalizeApiKey = (apiKey: string): string =>
  apiKey.startsWith(CHAT_API_KEY_PREFIX) ? apiKey.slice(CHAT_API_KEY_PREFIX.length) : apiKey;

/**
 * Percent-encodes everything outside `[A-Za-z0-9_-]`, so any api key yields a
 * valid cookie name token.
 *
 * @param value text to embed in a cookie name
 * @returns the cookie-name-safe text
 */
const toCookieToken = (value: string): string =>
  encodeURIComponent(value).replace(
    /[!'()*.~]/g,
    (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`,
  );

/**
 * Builds the cookie names for one chat client.
 *
 * @param apiKey chat client api key as passed to the widget, with or without prefix
 * @returns the scoped cookie names for this chat client
 */
export const cookieNamesFor = (apiKey: string): ChatbotCookieNames => {
  const scope = toCookieToken(normalizeApiKey(apiKey));
  return {
    session: `omq_chatbot_session_${scope}`,
    userClosed: `omq_chatbot_closed_${scope}`,
    autoOpen: `omq_chatbot_auto_open_${scope}`,
  };
};

/**
 * Builds the `document.cookie` assignment for one cookie: host-only (no
 * `Domain`), whole site (`Path=/`), first-party navigations only
 * (`SameSite=Lax`), and `Secure` on https pages.
 *
 * @param name cookie name
 * @param value raw value, URI-encoded here
 * @param maxAgeSeconds lifetime; 0 deletes the cookie
 * @param secure whether to add the `Secure` attribute
 * @returns the string to assign to `document.cookie`
 */
export const buildCookie = (
  name: string,
  value: string,
  maxAgeSeconds: number,
  secure: boolean,
): string => {
  const expires = new Date(Date.now() + maxAgeSeconds * 1000).toUTCString();
  return `${name}=${encodeURIComponent(value)}; Max-Age=${maxAgeSeconds}; Expires=${expires}; Path=/; SameSite=Lax${
    secure ? '; Secure' : ''
  }`;
};

/**
 * Cookie access that degrades to "nothing stored" when cookies are unavailable
 * (blocked, sandboxed iframe, …); the chat then lives in memory only.
 */
export const safeCookies = {
  /**
   * Reads a cookie value.
   *
   * @param name cookie name
   * @returns the decoded value, or null when absent or unreadable
   */
  get(name: string): string | null {
    try {
      const prefix = `${name}=`;
      const entry = document.cookie.split('; ').find((part) => part.startsWith(prefix));
      return entry ? decodeURIComponent(entry.slice(prefix.length)) || null : null;
    } catch {
      return null;
    }
  },
  /**
   * Writes a cookie, silently doing nothing when cookies are unavailable.
   *
   * @param name cookie name
   * @param value value to store
   * @param maxAgeSeconds lifetime in seconds
   */
  set(name: string, value: string, maxAgeSeconds: number): void {
    try {
      document.cookie = buildCookie(name, value, maxAgeSeconds, window.location.protocol === 'https:');
    } catch {
      // cookies unavailable — state won't persist across reloads
    }
  },
  /**
   * Deletes a cookie, silently doing nothing when cookies are unavailable.
   *
   * @param name cookie name
   */
  remove(name: string): void {
    safeCookies.set(name, '', 0);
  },
};

/**
 * localStorage access for reading and removing pre-cookie entries; unavailable
 * storage reads as empty.
 */
const legacyStorage = {
  /**
   * Reads a localStorage value.
   *
   * @param key storage key to read
   * @returns the stored value, or null when absent or storage is unavailable
   */
  get(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  /**
   * Removes a localStorage value, silently doing nothing when storage is unavailable.
   *
   * @param key storage key to remove
   */
  remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {
      // storage unavailable — nothing to remove
    }
  },
};

/**
 * The localStorage keys one chat client used between #2503 and #2983.
 *
 * @param apiKey chat client api key
 * @returns the scoped localStorage keys
 */
const scopedLocalStorageKeysFor = (apiKey: string) => {
  const scope = normalizeApiKey(apiKey);
  return {
    session: `${LEGACY_KEYS.session}:${scope}`,
    autoOpen: `${LEGACY_KEYS.autoOpen}:${scope}`,
    userClosed: `${LEGACY_KEYS.userClosed}:${scope}`,
  };
};

/** Browser-stored state of one chat client. */
export interface ChatbotStorage {
  /** Moves this chat client's pre-cookie localStorage state into cookies and deletes the old entries. */
  migrateLegacyStorage(): void;
  /** Session id to offer the relay for restore, or null. */
  readSession(): string | null;
  /** Stores a session id with a fresh lifetime, renewing the closed state alongside it. */
  saveSession(sessionId: string): void;
  /** Extends the lifetime only while the cookie still holds this session id. */
  renewSession(sessionId: string): void;
  /** Forgets the session id and the closed state. */
  clearSession(): void;
  /** Whether the visitor closed the chat window during the current session. */
  isUserClosed(): boolean;
  /** Records or forgets that the visitor closed the chat window. */
  setUserClosed(closed: boolean): void;
  /** Asks the next page load to open the chat window. */
  markAutoOpen(): void;
  /** Reads and clears the auto-open request. */
  consumeAutoOpen(): boolean;
}

/**
 * Creates the storage accessor for one chat client.
 *
 * @param apiKey chat client api key as passed to the widget
 * @returns the accessor
 */
export const chatbotStorageFor = (apiKey: string): ChatbotStorage => {
  const names = cookieNamesFor(apiKey);
  const oldKeys = scopedLocalStorageKeysFor(apiKey);

  /**
   * Writes the session cookie with a fresh lifetime and renews the closed state with it.
   *
   * @param sessionId session id to store
   */
  const writeSession = (sessionId: string): void => {
    safeCookies.set(names.session, sessionId, SESSION_MAX_AGE_SECONDS);
    if (safeCookies.get(names.userClosed) === 'true') {
      safeCookies.set(names.userClosed, 'true', SESSION_MAX_AGE_SECONDS);
    }
  };

  return {
    /**
     * Runs on every widget load, whether or not the chat is opened, so no old
     * entry outlives the first page view. A localStorage session only ever moves
     * into a fresh cookie and is deleted in the same step, so once that cookie
     * expires or is deleted nothing can bring the id back. Pre-#2503 unscoped
     * entries are deleted without being adopted: they cannot be attributed to a
     * chat client and are far older than any session the relay still holds.
     * Other chat clients' scoped entries are left for their own widget to move.
     */
    migrateLegacyStorage() {
      if (!safeCookies.get(names.session)) {
        const stored = legacyStorage.get(oldKeys.session);
        if (stored) {
          safeCookies.set(names.session, stored, SESSION_MAX_AGE_SECONDS);
          if (legacyStorage.get(oldKeys.userClosed) === 'true') {
            safeCookies.set(names.userClosed, 'true', SESSION_MAX_AGE_SECONDS);
          }
        }
      }
      [...Object.values(oldKeys), ...Object.values(LEGACY_KEYS)].forEach(legacyStorage.remove);
    },

    readSession: () => safeCookies.get(names.session),

    saveSession: writeSession,

    /**
     * Every tab of the site shares the cookie. A tab still holding an older
     * session in memory must neither bring back a cookie another tab deleted
     * (end of chat) nor overwrite the newer session another tab stored
     * (restart), so only a cookie that still holds this very id is renewed.
     */
    renewSession(sessionId) {
      if (safeCookies.get(names.session) === sessionId) {
        writeSession(sessionId);
      }
    },

    clearSession() {
      safeCookies.remove(names.session);
      safeCookies.remove(names.userClosed);
    },

    isUserClosed: () => safeCookies.get(names.userClosed) === 'true',

    setUserClosed(closed) {
      if (closed) {
        safeCookies.set(names.userClosed, 'true', SESSION_MAX_AGE_SECONDS);
      } else {
        safeCookies.remove(names.userClosed);
      }
    },

    markAutoOpen() {
      safeCookies.set(names.autoOpen, 'true', AUTO_OPEN_MAX_AGE_SECONDS);
    },

    consumeAutoOpen() {
      const requested = safeCookies.get(names.autoOpen) === 'true';
      if (requested) {
        safeCookies.remove(names.autoOpen);
      }
      return requested;
    },
  };
};
